import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { IndianRupee } from 'lucide-react'; // Add this import

const SolutionSection = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  const solutions = [
    {
      id: 1,
      title: "Vetted Talent Network",
      subtitle: "Pre-screened professionals you can trust",
      icon: "Shield",
      color: "blue",
      benefits: [
        "Rigorous 5-step screening process",
        "Portfolio verification & skill testing",
        "Background checks & references",
        "Ongoing performance monitoring",
        "Client feedback integration"
      ],
      stats: {
        value: "98%",
        label: "Success Rate"
      },
      description: "Every freelancer in our network undergoes comprehensive vetting including skill assessments, portfolio reviews, and client reference checks."
    },
    {
      id: 2,
      title: "Transparent Pricing",
      subtitle: "No hidden costs or surprise fees",
      icon: "IndianRupee",
      color: "green",
      benefits: [
        "Fixed-price project quotes",
        "Detailed cost breakdowns",
        "No markup on freelancer rates",
        "Money-back guarantee",
        "Escrow payment protection"
      ],
      stats: {
        value: "₹0",
        label: "Hidden Fees"
      },
      description: "Get upfront pricing with detailed breakdowns. What you see is what you pay - no surprises, no hidden costs, no scope creep."
    },
    {
      id: 3,
      title: "Guaranteed Delivery",
      subtitle: "On-time, on-budget, or your money back",
      icon: "CheckCircle",
      color: "purple",
      benefits: [
        "Milestone-based project tracking",
        "24/7 project management support",
        "Quality assurance reviews",
        "Unlimited revisions included",
        "100% satisfaction guarantee"
      ],
      stats: {
        value: "24hrs",
        label: "Response Time"
      },
      description: "Our project management system ensures timely delivery with quality checkpoints and dedicated support throughout your project."
    }
  ];

  const getColorClasses = (color, isHovered) => {
    const colors = {
      blue: {
        bg: isHovered ? 'bg-blue-600' : 'bg-blue-50',
        text: isHovered ? 'text-white' : 'text-blue-600',
        icon: isHovered ? 'text-white' : 'text-blue-600',
        border: 'border-blue-200',
        hover: 'hover:border-blue-300'
      },
      green: {
        bg: isHovered ? 'bg-green-600' : 'bg-green-50',
        text: isHovered ? 'text-white' : 'text-green-600',
        icon: isHovered ? 'text-white' : 'text-green-600',
        border: 'border-green-200',
        hover: 'hover:border-green-300'
      },
      purple: {
        bg: isHovered ? 'bg-purple-600' : 'bg-purple-50',
        text: isHovered ? 'text-white' : 'text-purple-600',
        icon: isHovered ? 'text-white' : 'text-purple-600',
        border: 'border-purple-200',
        hover: 'hover:border-purple-300'
      }
    };
    return colors[color];
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-green-100 text-green-800 text-sm font-medium mb-6">
            <Icon name="CheckCircle" size={16} className="mr-2" />
            The FreelanceHub Solution
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-poppins text-gray-900 mb-6">
            How We Solve Your
            <span className="text-blue-600"> Freelance Problems</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our three-pillar approach eliminates the common pain points of freelance hiring, giving you peace of mind and guaranteed results.
          </p>
        </div>

        {/* Solution Cards Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {solutions.map((solution) => {
            const isHovered = hoveredCard === solution.id;
            const colorClasses = getColorClasses(solution.color, isHovered);
            
            return (
              <div
                key={solution.id}
                className={`relative bg-white rounded-3xl border-2 ${colorClasses.border} ${colorClasses.hover} shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer`}
                onMouseEnter={() => setHoveredCard(solution.id)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                {/* Background Overlay */}
                <div className={`absolute inset-0 ${colorClasses.bg} transition-all duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}></div>
                
                <div className="relative z-10 p-8">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className={`w-16 h-16 rounded-2xl ${isHovered ? 'bg-white bg-opacity-20' : colorClasses.bg} flex items-center justify-center transition-all duration-300`}>
                      <Icon name={solution.icon} size={32} className={`${colorClasses.icon} transition-colors duration-300`} />
                    </div>
                    <div className="text-right">
                      <div className={`text-2xl font-bold ${isHovered ? 'text-white' : colorClasses.text} transition-colors duration-300`}>
                        {solution.stats.value}
                      </div>
                      <div className={`text-sm ${isHovered ? 'text-white text-opacity-80' : 'text-gray-600'} transition-colors duration-300`}>
                        {solution.stats.label}
                      </div>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className={`text-2xl font-bold font-poppins mb-2 ${isHovered ? 'text-white' : 'text-gray-900'} transition-colors duration-300`}>
                    {solution.title}
                  </h3>
                  <p className={`text-lg mb-6 ${isHovered ? 'text-white text-opacity-90' : 'text-gray-600'} transition-colors duration-300`}>
                    {solution.subtitle}
                  </p>

                  {/* Description */}
                  <p className={`mb-6 leading-relaxed ${isHovered ? 'text-white text-opacity-80' : 'text-gray-600'} transition-colors duration-300`}>
                    {solution.description}
                  </p>

                  {/* Benefits List */}
                  <div className="space-y-3">
                    {solution.benefits.map((benefit, index) => (
                      <div key={index} className="flex items-start">
                        <Icon 
                          name="Check" 
                          size={16} 
                          className={`mr-3 mt-1 ${isHovered ? 'text-white' : colorClasses.icon} transition-colors duration-300`} 
                        />
                        <span className={`text-sm ${isHovered ? 'text-white text-opacity-90' : 'text-gray-600'} transition-colors duration-300`}>
                          {benefit}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Hover Arrow */}
                  <div className={`absolute bottom-6 right-6 transform transition-all duration-300 ${isHovered ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'}`}>
                    <Icon name="ArrowRight" size={24} className="text-white" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Stats */}
        <div className="bg-gray-50 rounded-3xl p-8 lg:p-12">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">5000+</div>
              <div className="text-gray-600 font-medium">Successful Projects</div>
              <div className="text-sm text-gray-500 mt-1">Delivered on time and budget</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-green-600 mb-2">98%</div>
              <div className="text-gray-600 font-medium">Client Satisfaction</div>
              <div className="text-sm text-gray-500 mt-1">Based on verified reviews</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-purple-600 mb-2">24hrs</div>
              <div className="text-gray-600 font-medium">Average Response</div>
              <div className="text-sm text-gray-500 mt-1">From inquiry to proposal</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;